import {
  forwardRef,
  useCallback,
  useEffect,
  useRef,
  useState,
  type ChangeEvent,
  type KeyboardEvent,
} from 'react';
import { Check, ChevronDown, Search } from 'lucide-react';
import {
  PHONE_COUNTRIES,
  findPhoneCountry,
  type PhoneCountry,
} from '@shared/constants/phone-codes';
import { cn } from '../../../lib/cn';

export interface PhoneInputValue {
  countryIso2: string;
  countryDial: string;
  national: string;
}

export interface PhoneInputProps {
  id?: string;
  value: PhoneInputValue;
  onChange: (value: PhoneInputValue) => void;
  error?: string;
  disabled?: boolean;
  required?: boolean;
}

export const PhoneInput = forwardRef<HTMLInputElement, PhoneInputProps>(
  ({ id, value, onChange, error, disabled, required }, ref) => {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState('');
    const buttonRef = useRef<HTMLButtonElement>(null);
    const popoverRef = useRef<HTMLDivElement>(null);
    const searchRef = useRef<HTMLInputElement>(null);
    const country: PhoneCountry =
      findPhoneCountry(value.countryIso2) ?? PHONE_COUNTRIES[0]!;

    const filtered = PHONE_COUNTRIES.filter((c) => {
      const needle = search.trim().toLowerCase();
      if (!needle) return true;
      return (
        c.name.toLowerCase().includes(needle) ||
        c.dial.startsWith(needle.replace(/^\+/, '')) ||
        c.iso2.toLowerCase().includes(needle)
      );
    });

    const closeAndFocus = useCallback(() => {
      setOpen(false);
      setSearch('');
      buttonRef.current?.focus();
    }, []);

    useEffect(() => {
      if (!open) return;
      function handleClickOutside(e: MouseEvent): void {
        if (
          popoverRef.current &&
          !popoverRef.current.contains(e.target as Node) &&
          buttonRef.current &&
          !buttonRef.current.contains(e.target as Node)
        ) {
          setOpen(false);
          setSearch('');
        }
      }
      function handleKey(e: globalThis.KeyboardEvent): void {
        if (e.key === 'Escape') {
          closeAndFocus();
        }
      }
      window.addEventListener('mousedown', handleClickOutside);
      window.addEventListener('keydown', handleKey);
      const focusTimer = window.setTimeout(() => {
        searchRef.current?.focus();
      }, 10);
      return () => {
        window.removeEventListener('mousedown', handleClickOutside);
        window.removeEventListener('keydown', handleKey);
        window.clearTimeout(focusTimer);
      };
    }, [open, closeAndFocus]);

    function pickCountry(c: PhoneCountry): void {
      onChange({
        countryIso2: c.iso2,
        countryDial: c.dial,
        national: value.national,
      });
      closeAndFocus();
    }

    function handleNumberChange(e: ChangeEvent<HTMLInputElement>): void {
      const raw = e.target.value;
      const cleaned = raw.replace(/[^\d\s()\-.]/g, '').slice(0, 24);
      onChange({
        countryIso2: country.iso2,
        countryDial: country.dial,
        national: cleaned,
      });
    }

    function handleListKey(e: KeyboardEvent<HTMLDivElement>): void {
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        const focusables = popoverRef.current?.querySelectorAll<HTMLElement>(
          '[data-country-item="true"]',
        );
        if (!focusables || focusables.length === 0) return;
        const arr = Array.from(focusables);
        const currentIdx = arr.findIndex((el) => el === document.activeElement);
        const nextIdx =
          e.key === 'ArrowDown'
            ? (currentIdx + 1) % arr.length
            : (currentIdx - 1 + arr.length) % arr.length;
        arr[nextIdx]?.focus();
      }
    }

    return (
      <div className="relative space-y-1.5">
        <div
          className={cn(
            'group relative flex w-full overflow-hidden rounded-xl border-2 bg-white transition-colors',
            error
              ? 'border-red-300 focus-within:border-red-400'
              : 'border-stone-200 focus-within:border-brand-400',
            disabled && 'opacity-60',
          )}
        >
          <button
            ref={buttonRef}
            type="button"
            aria-label="Indicatif téléphonique"
            aria-haspopup="listbox"
            aria-expanded={open}
            disabled={disabled}
            onClick={() => setOpen((v) => !v)}
            className={cn(
              'flex shrink-0 items-center gap-2 border-r border-stone-200 bg-stone-50 px-3 text-[13px] font-semibold text-stone-700 transition-colors hover:bg-stone-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-400 disabled:cursor-not-allowed',
            )}
          >
            <span className="text-lg leading-none" aria-hidden="true">
              {country.flag}
            </span>
            <span className="tabular-nums">+{country.dial}</span>
            <ChevronDown
              className={cn(
                'h-3 w-3 text-stone-400 transition-transform duration-200',
                open && 'rotate-180',
              )}
            />
          </button>

          <input
            ref={ref}
            id={id}
            type="tel"
            inputMode="tel"
            autoComplete="tel-national"
            disabled={disabled}
            required={required}
            placeholder={country.example}
            value={value.national}
            onChange={handleNumberChange}
            className="h-12 flex-1 bg-transparent px-3 text-[15px] tabular-nums text-ink placeholder:text-stone-300 focus:outline-none"
          />
        </div>

        {open && (
          <div
            ref={popoverRef}
            role="listbox"
            aria-label="Sélectionner un pays"
            tabIndex={-1}
            onKeyDown={handleListKey}
            className="absolute left-0 z-30 mt-1 w-full max-w-72 origin-top-left overflow-hidden rounded-xl border border-stone-200 bg-white shadow-[0_8px_32px_rgba(0,0,0,.12)]"
          >
            <div className="border-b border-stone-100 p-2">
              <div className="relative">
                <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-stone-400" />
                <input
                  ref={searchRef}
                  type="search"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Rechercher un pays…"
                  className="h-9 w-full rounded-lg border border-stone-200 bg-stone-50 pl-8 pr-2 text-[13px] text-ink placeholder:text-stone-400 focus:border-brand-400 focus:bg-white focus:outline-none"
                />
              </div>
            </div>
            <div className="max-h-64 overflow-y-auto py-1" role="presentation">
              {filtered.length === 0 ? (
                <p className="px-3 py-4 text-center text-[12px] text-stone-500">
                  Aucun pays trouvé
                </p>
              ) : (
                filtered.map((c) => {
                  const selected = c.iso2 === country.iso2;
                  return (
                    <button
                      key={c.iso2}
                      type="button"
                      role="option"
                      aria-selected={selected}
                      data-country-item="true"
                      onClick={() => pickCountry(c)}
                      className={cn(
                        'flex w-full items-center gap-2.5 px-3 py-2 text-left text-[13px] transition-colors hover:bg-stone-50 focus:bg-stone-100 focus:outline-none',
                        selected && 'bg-brand-50/60 font-semibold text-brand-800',
                      )}
                    >
                      <span className="text-lg leading-none" aria-hidden="true">
                        {c.flag}
                      </span>
                      <span className="flex-1 truncate text-stone-700">{c.name}</span>
                      <span className="shrink-0 tabular-nums text-[12px] text-stone-500">
                        +{c.dial}
                      </span>
                      {selected && (
                        <Check className="h-3.5 w-3.5 shrink-0 text-brand-600" />
                      )}
                    </button>
                  );
                })
              )}
            </div>
          </div>
        )}

        {error && (
          <p className="text-[12px] font-medium text-red-600" role="alert">
            {error}
          </p>
        )}
      </div>
    );
  },
);

PhoneInput.displayName = 'PhoneInput';
